/* ───────────────────────────────────────────────────────────────────────────
   TUAP onboarding — form schema + Firestore/Storage helpers.

   When a student converts to TUAP after the First Meeting, they complete an
   onboarding form (formerly a ~110-field Google Form). It is modeled here as
   chapters, each split into short SCREENS (one question or a tight group).
   OnboardingWizard.jsx renders one screen at a time, Duolingo-style.

   Collection: `tuapStudents` — one doc per TUAP student. Same open-rules
   posture as `leads` for now; auth lockdown comes with the SSO workspace.
   ─────────────────────────────────────────────────────────────────────────── */

import { db } from "./firebase";
import { sendMail } from "./mailer";
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc,
  onSnapshot, query, orderBy, where, serverTimestamp,
} from "firebase/firestore";

// The shared NM Squad WhatsApp group the bot posts handover updates into,
// and Ravisha's WhatsApp id for @-mentions in those messages.
const ONB_GROUP = "NM Squad™";
const OFFICE_CC = "mandhana.office@gmail.com";
const RAVISHA_WA = "917021707112@c.us";

function slugify(s) {
  return String(s || "student").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "student";
}
// Clean per-student portal URL (/firstname-lastname-xxxxx); old links still work.
function studentLink(s) {
  if (s.slug) return `${window.location.origin}/${s.slug}`;
  return `${window.location.origin}/student?id=${s.id}&t=${s.accessToken}`;
}

/* File storage — onboarding files are compressed in the browser and stored
   directly in Firestore (one doc per file in a `files` subcollection). No
   Firebase Storage, no Drive, no third party: nothing to enable or pay for.
   Each Firestore doc caps at ~1 MB, so images are downscaled below that. */
const FILE_LIMIT = 1000000; // max data-URL length per file

/* ── Pipeline the SSO moves a student through (handover → active) ── */
export const TUAP_PIPELINE = [
  "Handover",
  "Group Created",
  "ONB Sent",
  "ONB In Progress",
  "ONB Complete",
  "Profile Setup",
  "Active",
];

/* ── Option lists ── */
const PROFICIENCY = ["Native", "Fluent", "Advanced", "Intermediate", "Basic"];
const EDU_LEVELS = ["High School", "Diploma", "Bachelor's", "Master's", "Doctorate", "Other"];
const PREFIXES = ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."];
const YESNO = ["Yes", "No"];

/* Countries — used for the Duolingo-style flag dropdowns (birth, citizenship).
   `code` is ISO 3166-1 alpha-2 (lowercase) — feeds into flagcdn.com/w40/<code>.png.
   India first; the rest alphabetical. Covers everywhere our students realistically
   come from / apply to. */
export const COUNTRIES = [
  { code: "in", name: "India" },
  { code: "af", name: "Afghanistan" }, { code: "al", name: "Albania" },
  { code: "dz", name: "Algeria" }, { code: "ad", name: "Andorra" },
  { code: "ao", name: "Angola" }, { code: "ar", name: "Argentina" },
  { code: "am", name: "Armenia" }, { code: "au", name: "Australia" },
  { code: "at", name: "Austria" }, { code: "az", name: "Azerbaijan" },
  { code: "bh", name: "Bahrain" }, { code: "bd", name: "Bangladesh" },
  { code: "by", name: "Belarus" }, { code: "be", name: "Belgium" },
  { code: "bt", name: "Bhutan" }, { code: "bo", name: "Bolivia" },
  { code: "ba", name: "Bosnia and Herzegovina" }, { code: "bw", name: "Botswana" },
  { code: "br", name: "Brazil" }, { code: "bn", name: "Brunei" },
  { code: "bg", name: "Bulgaria" }, { code: "kh", name: "Cambodia" },
  { code: "cm", name: "Cameroon" }, { code: "ca", name: "Canada" },
  { code: "cl", name: "Chile" }, { code: "cn", name: "China" },
  { code: "co", name: "Colombia" }, { code: "cr", name: "Costa Rica" },
  { code: "hr", name: "Croatia" }, { code: "cu", name: "Cuba" },
  { code: "cy", name: "Cyprus" }, { code: "cz", name: "Czech Republic" },
  { code: "dk", name: "Denmark" }, { code: "do", name: "Dominican Republic" },
  { code: "ec", name: "Ecuador" }, { code: "eg", name: "Egypt" },
  { code: "ee", name: "Estonia" }, { code: "et", name: "Ethiopia" },
  { code: "fj", name: "Fiji" }, { code: "fi", name: "Finland" },
  { code: "fr", name: "France" }, { code: "ge", name: "Georgia" },
  { code: "de", name: "Germany" }, { code: "gh", name: "Ghana" },
  { code: "gr", name: "Greece" }, { code: "gt", name: "Guatemala" },
  { code: "hn", name: "Honduras" }, { code: "hk", name: "Hong Kong" },
  { code: "hu", name: "Hungary" }, { code: "is", name: "Iceland" },
  { code: "id", name: "Indonesia" }, { code: "ir", name: "Iran" },
  { code: "iq", name: "Iraq" }, { code: "ie", name: "Ireland" },
  { code: "il", name: "Israel" }, { code: "it", name: "Italy" },
  { code: "jm", name: "Jamaica" }, { code: "jp", name: "Japan" },
  { code: "jo", name: "Jordan" }, { code: "kz", name: "Kazakhstan" },
  { code: "ke", name: "Kenya" }, { code: "kw", name: "Kuwait" },
  { code: "kg", name: "Kyrgyzstan" }, { code: "la", name: "Laos" },
  { code: "lv", name: "Latvia" }, { code: "lb", name: "Lebanon" },
  { code: "ly", name: "Libya" }, { code: "lt", name: "Lithuania" },
  { code: "lu", name: "Luxembourg" }, { code: "mo", name: "Macau" },
  { code: "mg", name: "Madagascar" }, { code: "my", name: "Malaysia" },
  { code: "mv", name: "Maldives" }, { code: "mt", name: "Malta" },
  { code: "mu", name: "Mauritius" }, { code: "mx", name: "Mexico" },
  { code: "md", name: "Moldova" }, { code: "mc", name: "Monaco" },
  { code: "mn", name: "Mongolia" }, { code: "me", name: "Montenegro" },
  { code: "ma", name: "Morocco" }, { code: "mz", name: "Mozambique" },
  { code: "mm", name: "Myanmar" }, { code: "na", name: "Namibia" },
  { code: "np", name: "Nepal" }, { code: "nl", name: "Netherlands" },
  { code: "nz", name: "New Zealand" }, { code: "ng", name: "Nigeria" },
  { code: "kp", name: "North Korea" }, { code: "mk", name: "North Macedonia" },
  { code: "no", name: "Norway" }, { code: "om", name: "Oman" },
  { code: "pk", name: "Pakistan" }, { code: "ps", name: "Palestine" },
  { code: "pa", name: "Panama" }, { code: "py", name: "Paraguay" },
  { code: "pe", name: "Peru" }, { code: "ph", name: "Philippines" },
  { code: "pl", name: "Poland" }, { code: "pt", name: "Portugal" },
  { code: "qa", name: "Qatar" }, { code: "ro", name: "Romania" },
  { code: "ru", name: "Russia" }, { code: "rw", name: "Rwanda" },
  { code: "sa", name: "Saudi Arabia" }, { code: "sn", name: "Senegal" },
  { code: "rs", name: "Serbia" }, { code: "sg", name: "Singapore" },
  { code: "sk", name: "Slovakia" }, { code: "si", name: "Slovenia" },
  { code: "za", name: "South Africa" }, { code: "kr", name: "South Korea" },
  { code: "es", name: "Spain" }, { code: "lk", name: "Sri Lanka" },
  { code: "sd", name: "Sudan" }, { code: "se", name: "Sweden" },
  { code: "ch", name: "Switzerland" }, { code: "sy", name: "Syria" },
  { code: "tw", name: "Taiwan" }, { code: "tj", name: "Tajikistan" },
  { code: "tz", name: "Tanzania" }, { code: "th", name: "Thailand" },
  { code: "tn", name: "Tunisia" }, { code: "tr", name: "Turkey" },
  { code: "tm", name: "Turkmenistan" }, { code: "ug", name: "Uganda" },
  { code: "ua", name: "Ukraine" }, { code: "ae", name: "United Arab Emirates" },
  { code: "gb", name: "United Kingdom" }, { code: "us", name: "United States" },
  { code: "uy", name: "Uruguay" }, { code: "uz", name: "Uzbekistan" },
  { code: "ve", name: "Venezuela" }, { code: "vn", name: "Vietnam" },
  { code: "ye", name: "Yemen" }, { code: "zm", name: "Zambia" },
  { code: "zw", name: "Zimbabwe" },
];
export const COUNTRY_NAMES = COUNTRIES.map(c => c.name);
export function countryCode(name) {
  const c = COUNTRIES.find(x => x.name === name);
  return c ? c.code : "";
}

/* Interest Mapping — the real NM Squad survey: 50 interests, 3-point scale. */
export const INTEREST_OPTIONS = ["Not Interested", "Somewhat Interested", "Very Interested"];
export const INTEREST_SUBJECTS = [
  "Computer Programming", "Astronomy", "Mathematics", "Puzzles", "Quizzing",
  "Console & Computer Games", "Rubik's Cube", "Gadgets & Technology", "Robotics",
  "Lego", "Chess", "Aeromodelling", "Origami", "Sculpture & Pottery", "Dance",
  "Cartoons, Comics or Anime", "Community Service", "Teaching",
  "Collecting Things (Notes, Coins etc.)", "Fashion, Make up or Shopping", "DJ-ing",
  "Rapping or Beat Boxing", "Acting or Theatre", "Films or TV Shows",
  "Interior & Home Decor", "Photography", "Singing", "Music - Instrumental", "Magic",
  "Environment & Nature", "Pet Care", "Painting", "Sketching or Drawing",
  "Managing Events", "Socializing", "Leadership", "Adventure Sports", "Outdoor Sports",
  "Playing Cards", "Food, Cooking or Eating Out", "Travelling",
  "Public Speaking & Debating", "Reading", "Writing or Poetry",
  "Learning Foreign Languages", "Make Up", "Calligraphy", "Horse Riding",
  "Mythology", "Gardening",
];

/* Community Service Mapping — 3-point scale. */
export const COMMUNITY_OPTIONS = ["Yes", "Maybe", "No"];
export const COMMUNITY_ISSUES = [
  "Animal Rights", "Anti-Bullying", "Border & Resource Disputes", "Censorship",
  "Cheating & Financial Fraud", "Child Labor", "City Cleanliness", "Clean Water",
  "Corruption", "Crime & Violence", "Cybercrime", "Depression", "Digital Addiction",
  "Disability Support", "Domestic Violence", "Donation & Charity", "Dowry",
  "Drug & Alcohol Addiction", "Elder Care", "Fake News", "Fake Products", "Gambling",
  "Gender Issues & Inequality", "Human Trafficking & Prostitution", "Illiteracy",
  "Infrastructure Development", "LGBT Rights", "Make in India", "Medical Care",
  "Natural Disasters", "Overpopulation", "Personal Health & Fitness",
  "Personal Money Management", "Planting Trees", "Saving Wildlife", "Poverty & Hunger",
  "Racism & Discrimination", "Rape", "Recycling & Sustainability", "Religious Conflicts",
  "Right To Freedom & Free Speech", "Rural Development", "Sanitation & Waste Management",
  "Saving Your Culture", "Sports Awareness", "Teaching & Mentoring", "Terrorism",
  "Traffic & Pollution", "Training & Skill Building", "Unemployment",
  "Upgrading Laws & Constitution", "Urban Planning",
];

/* Documents collected in the Documents chapter.
   Grade marksheets are only shown for grades the student has actually reached
   (Grade 9 → just 9, Grade 11 → 9/10/11, Grade 12/Gap → all four).
   `showIf(values)` gates a document from the upload grid. */
const gradeReached = (v, n) => {
  const g = v.currentGrade || "";
  if (g === "Gap year" || g === "Grade 12") return n <= 12;
  if (g === "Grade 11") return n <= 11;
  if (g === "Grade 10") return n <= 10;
  if (g === "Grade 9") return n <= 9;
  return false;
};
// Every visible doc is required. If the student doesn't have it yet, they can
// flag the tile as "I'll submit it later" — that counts as satisfying the
// requirement (stored in `values.docsDeferred[key] === true`).
export const ONB_DOCUMENTS = [
  { key: "picture", label: "Your most recent photo", required: true, hint: "A clear, recent headshot." },
  { key: "passportFront", label: "Passport — front page", required: true },
  { key: "passportBack", label: "Passport — back page", required: true },
  { key: "usVisa", label: "U.S. Visa", required: true },
  { key: "marksheet9",  label: "Grade 9 marksheet",  required: true, showIf: v => gradeReached(v, 9) },
  { key: "marksheet10", label: "Grade 10 marksheet", required: true, showIf: v => gradeReached(v, 10) },
  { key: "marksheet11", label: "Grade 11 marksheet", required: true, showIf: v => gradeReached(v, 11) },
  { key: "marksheet12", label: "Grade 12 marksheet", required: true, showIf: v => gradeReached(v, 12) },
  { key: "satScore", label: "SAT scorecard", required: true },
  { key: "actScore", label: "ACT scorecard", required: true },
  { key: "apScore", label: "AP scorecard", required: true },
  { key: "toeflScore", label: "TOEFL scorecard", required: true },
  { key: "ieltsScore", label: "IELTS scorecard", required: true },
  { key: "duolingoScore", label: "Duolingo scorecard", required: true },
];

// Documents currently visible given the student's answers (handles showIf).
export function visibleDocuments(values) {
  return ONB_DOCUMENTS.filter(d => !d.showIf || d.showIf(values));
}

/* ── The onboarding form, as chapters of short screens ─────────────────────
   chapter: { id, title, screens:[...] }
   screen:  { id, q, sub?, say?, showIf?(v), fields?:[field], kind? }
            kind "interests" | "documents" render specially.
   field:   { key, label, type, options?, required?, placeholder?, help?, showIf? }
   field types: text | textarea | email | tel | number | date | select | radio  */

const langScreen = (n) => ({
  id: `lang${n}`,
  q: n === 1 ? "Your first language" : `Language number ${n}`,
  showIf: v => Number(v.numLanguages) >= n,
  fields: [
    { key: `language${n}`, label: "Language", type: "text", required: true, placeholder: "e.g. Hindi" },
    { key: `language${n}Prof`, label: "How well do you speak it?", type: "select",
      options: PROFICIENCY, required: true },
  ],
});

function parentScreens(who) {
  const p = who.toLowerCase();
  const alive = `${p}Alive`;
  const on = v => v[alive] === "Yes";
  const numKey = `${p}NumInst`;
  return [
    { id: `${p}Alive`, q: `Is your ${p} alive?`,
      say: who === "Father" ? "Now a little about your parents." : undefined,
      fields: [{ key: alive, label: `${who} is alive`, type: "radio", options: YESNO, required: true }] },
    { id: `${p}Name`, q: `Your ${p}'s name`, showIf: on,
      sub: "As it appears on official documents.",
      fields: [
        { key: `${p}Prefix`, label: "Prefix", type: "select", options: PREFIXES, required: true },
        { key: `${p}First`, label: "First name", type: "text", required: true },
        { key: `${p}Middle`, label: "Middle name (write NA if none)", type: "text", required: true },
        { key: `${p}Last`, label: "Last name", type: "text", required: true },
      ] },
    { id: `${p}Work`, q: `What does your ${p} do?`, showIf: on,
      fields: [
        { key: `${p}Occupation`, label: "Occupation", type: "text", required: true },
        { key: `${p}Phone`, label: "Phone number (write NA if not available)", type: "tel", required: true },
        { key: `${p}Education`, label: "Highest education level", type: "select", options: EDU_LEVELS, required: true },
      ] },
    { id: `${p}Edu`, q: `Your ${p}'s education`, showIf: on,
      sub: "Where did they study? Pick 0 if you're not sure of the institutions.",
      fields: [
        { key: numKey, label: "Institutions attended", type: "select", options: ["0", "1", "2"], required: true },
        { key: `${p}Inst1`, label: "Institution 1 — name", type: "text", required: true,
          showIf: v => Number(v[numKey]) >= 1 },
        { key: `${p}Inst1Degree`, label: "Institution 1 — degree", type: "text", required: true,
          showIf: v => Number(v[numKey]) >= 1 },
        { key: `${p}Inst1Year`, label: "Institution 1 — year", type: "text", required: true,
          showIf: v => Number(v[numKey]) >= 1 },
        { key: `${p}Inst2`, label: "Institution 2 — name", type: "text", required: true,
          showIf: v => Number(v[numKey]) >= 2 },
        { key: `${p}Inst2Degree`, label: "Institution 2 — degree", type: "text", required: true,
          showIf: v => Number(v[numKey]) >= 2 },
        { key: `${p}Inst2Year`, label: "Institution 2 — year", type: "text", required: true,
          showIf: v => Number(v[numKey]) >= 2 },
      ] },
  ];
}

export const ONB_CHAPTERS = [
  {
    id: "about",
    title: "About you",
    screens: [
      { id: "name", q: "What's your name?",
        sub: "Exactly as it should appear on your college applications.",
        say: "Hi! I'm so glad you're here. Let's start nice and simple.",
        fields: [
          { key: "firstName", label: "First name", type: "text", required: true, placeholder: "First name" },
          { key: "middleName", label: "Middle name (write NA if none)", type: "text", required: true,
            placeholder: "Middle name or NA" },
          { key: "lastName", label: "Last / family name", type: "text", required: true, placeholder: "Last name" },
        ] },
      { id: "dob", q: "When's your birthday?",
        say: "No need to celebrate yet — just the date.",
        fields: [{ key: "dob", label: "Date of birth", type: "date", required: true }] },
      { id: "gender", q: "How do you identify?",
        fields: [{ key: "gender", label: "Gender", type: "radio", required: true,
          options: ["Male", "Female", "Other", "Prefer not to say"] }] },
      { id: "phone", q: "What's your phone number?",
        say: "So your team can always reach you.",
        fields: [{ key: "studentPhone", label: "Phone number", type: "tel", required: true,
          placeholder: "Your mobile number" }] },
      { id: "email", q: "And your email address?",
        fields: [{ key: "email", label: "Email", type: "email", required: true,
          placeholder: "you@email.com" }] },
      { id: "address", q: "Where's home?",
        sub: "Your permanent home address.",
        fields: [{ key: "address", label: "Permanent home address", type: "textarea", required: true,
          placeholder: "House, street, city, state, pincode" }] },
      { id: "personal", q: "A couple of personal details",
        say: "These help colleges get to know the real you.",
        fields: [
          { key: "religion", label: "Religion (write NA if you'd rather not say)", type: "text",
            required: true, placeholder: "Religion or NA" },
          { key: "foodPref", label: "Food preference", type: "select", required: true,
            options: ["Vegetarian", "Non-vegetarian", "Vegan", "Eggetarian", "Jain", "Other"] },
        ] },
      { id: "health", q: "Anything about your health we should know?",
        sub: "Medical conditions, allergies, or a physical disability. Write NA if there are none.",
        fields: [
          { key: "medical", label: "Medical conditions or allergies", type: "textarea",
            required: true, placeholder: "Write NA if none" },
          { key: "disability", label: "Physical disability", type: "textarea",
            required: true, placeholder: "Write NA if none" },
        ] },
      { id: "ld", q: "Have you been tested for a learning disability?",
        fields: [{ key: "ldStatus", label: "LD testing status", type: "radio", required: true,
          options: ["Not tested", "Tested — no LD", "Tested — LD identified", "Prefer not to say"] }] },
    ],
  },
  {
    id: "languages",
    title: "Languages",
    screens: [
      { id: "numLang", q: "How many languages do you speak well?",
        say: "Speaking more than one is a real strength.",
        fields: [{ key: "numLanguages", label: "Number of languages", type: "radio", required: true,
          options: ["1", "2", "3", "4", "5"] }] },
      langScreen(1), langScreen(2), langScreen(3), langScreen(4), langScreen(5),
    ],
  },
  {
    id: "origin",
    title: "Origin & travel",
    screens: [
      { id: "birth", q: "Where were you born?",
        say: "Now a little about where you come from.",
        fields: [
          { key: "birthCountry", label: "Birth country", type: "country", required: true },
          { key: "birthCity", label: "City of birth", type: "text", required: true },
        ] },
      { id: "citizenship", q: "Your citizenship",
        fields: [
          { key: "citizenship", label: "Country of citizenship", type: "country", required: true },
          { key: "citizenship2", label: "Second citizenship (write NA if none)", type: "country-or-na", required: true },
          { key: "usYears", label: "Years lived in the United States (write NA if none)", type: "text", required: true,
            placeholder: "e.g. 0, or NA" },
        ] },
      { id: "hasVisa", q: "Do you hold a valid U.S. visa?",
        fields: [{ key: "hasVisa", label: "Holds a U.S. visa", type: "radio", required: true, options: YESNO }] },
      { id: "visa", q: "Tell us about your U.S. visa",
        showIf: v => v.hasVisa === "Yes",
        fields: [
          { key: "visaType", label: "Visa type", type: "text", required: true },
          { key: "visaNumber", label: "Visa number", type: "text", required: true },
          { key: "visaIssue", label: "Issue date", type: "date", required: true },
          { key: "visaExpiry", label: "Expiry date", type: "date", required: true },
        ] },
      { id: "hasPassport", q: "Do you have a passport?",
        fields: [{ key: "hasPassport", label: "Has a passport", type: "radio", required: true, options: YESNO }] },
      { id: "passport", q: "Your passport details",
        showIf: v => v.hasPassport === "Yes",
        fields: [
          { key: "passportNumber", label: "Passport number", type: "text", required: true },
          { key: "passportPlace", label: "Place of issue", type: "text", required: true },
          { key: "passportIssue", label: "Issue date", type: "date", required: true },
          { key: "passportExpiry", label: "Expiry date", type: "date", required: true },
        ] },
    ],
  },
  {
    id: "family",
    title: "Your family",
    screens: [
      { id: "marital", q: "Your parents' marital status",
        say: "Family next — answer as best you can.",
        fields: [{ key: "parentsMaritalStatus", label: "Parents' marital status", type: "radio", required: true,
          options: ["Married", "Divorced", "Separated", "Widowed"] }] },
      { id: "livesWith", q: "Who do you live with?",
        fields: [{ key: "livesWith", label: "I make my permanent home with", type: "radio", required: true,
          options: ["Mom", "Dad", "Both", "Guardian"] }] },
      ...parentScreens("Father"),
      ...parentScreens("Mother"),
      { id: "hasSiblings", q: "Do you have any siblings?",
        fields: [{ key: "hasSiblings", label: "Has siblings", type: "radio", required: true, options: YESNO }] },
      { id: "siblings", q: "Tell us about your siblings",
        showIf: v => v.hasSiblings === "Yes",
        fields: [
          { key: "numSiblings", label: "How many siblings?", type: "select",
            options: ["1", "2", "3", "4", "5"], required: true },
          { key: "sibling1First", label: "Sibling 1 — first name", type: "text", required: true, showIf: v => Number(v.numSiblings) >= 1 },
          { key: "sibling1Last", label: "Sibling 1 — last name", type: "text", required: true, showIf: v => Number(v.numSiblings) >= 1 },
          { key: "sibling1Age", label: "Sibling 1 — age", type: "number", required: true, showIf: v => Number(v.numSiblings) >= 1 },
          { key: "sibling2First", label: "Sibling 2 — first name", type: "text", required: true, showIf: v => Number(v.numSiblings) >= 2 },
          { key: "sibling2Last", label: "Sibling 2 — last name", type: "text", required: true, showIf: v => Number(v.numSiblings) >= 2 },
          { key: "sibling2Age", label: "Sibling 2 — age", type: "number", required: true, showIf: v => Number(v.numSiblings) >= 2 },
        ] },
      { id: "parentEmails", q: "Your parents' email addresses",
        say: "Almost through the family section!",
        // Only ask for an email when that parent is alive.
        showIf: v => v.fatherAlive === "Yes" || v.motherAlive === "Yes",
        fields: [
          { key: "fatherEmail", label: "Father's email", type: "email", required: true,
            showIf: v => v.fatherAlive === "Yes" },
          { key: "motherEmail", label: "Mother's email", type: "email", required: true,
            showIf: v => v.motherAlive === "Yes" },
        ] },
    ],
  },
  {
    id: "education",
    title: "Your education",
    screens: [
      { id: "grade", q: "What grade are you in right now?",
        say: "Let's map out your schooling.",
        fields: [{ key: "currentGrade", label: "Current grade", type: "radio", required: true,
          options: ["Grade 9", "Grade 10", "Grade 11", "Grade 12", "Gap year"] }] },
      { id: "schools", q: "Which schools have you attended?",
        sub: "Write NA for any grade you haven't reached yet.",
        fields: [
          { key: "school9",  label: "Grade 9 school",  type: "text", required: true, showIf: v => gradeReached(v, 9) },
          { key: "school10", label: "Grade 10 school", type: "text", required: true, showIf: v => gradeReached(v, 10) },
          { key: "school11", label: "Grade 11 school", type: "text", required: true, showIf: v => gradeReached(v, 11) },
          { key: "school12", label: "Grade 12 school", type: "text", required: true, showIf: v => gradeReached(v, 12) },
        ] },
      { id: "finish", q: "Finishing school",
        fields: [
          { key: "grade12Finish", label: "When will you finish Grade 12?", type: "text",
            required: true, placeholder: "Month and year" },
          { key: "boarding", label: "Are you at a boarding school?", type: "select",
            required: true, options: YESNO },
        ] },
      { id: "schoolMore", q: "A couple more on your schooling",
        fields: [
          { key: "gapPeriod", label: "Any gap period or discontinuation?", type: "textarea",
            required: true, placeholder: "Write NA if none" },
          { key: "admissionsSoftware", label: "Admissions software your school uses (Cialfo, BridgeU...)",
            type: "text", required: true, placeholder: "Name it, or write NA" },
        ] },
      { id: "major", q: "What do you want to major in?",
        say: "Dream big — you can always change your mind later.",
        fields: [{ key: "intendedMajor", label: "Intended major", type: "text", required: true,
          placeholder: "e.g. Computer Science" }] },
    ],
  },
  {
    id: "interests",
    title: "Interest mapping",
    screens: [
      { id: "interests", kind: "interests", q: "What are you into?",
        sub: "Rate how interested you are in each area. Be honest — there are no wrong answers.",
        say: "This is my favourite part. Give every one a rating." },
    ],
  },
  {
    id: "community",
    title: "Community service",
    screens: [
      { id: "community", kind: "community", q: "Causes you care about",
        sub: "Which of these would you volunteer for, or raise awareness for? There are no wrong answers.",
        say: "Tell me what matters to you — this shapes your community story." },
    ],
  },
  {
    id: "documents",
    title: "Documents",
    screens: [
      { id: "documents", kind: "documents", q: "Upload your documents",
        sub: "Add what you have now. You can skip anything and add it later.",
        say: "A clear photo of each document works perfectly — no scanner needed." },
    ],
  },
  {
    id: "review",
    title: "Services Agreement",
    screens: [
      { id: "mou", kind: "mou", q: "Read our Services Agreement",
        sub: "Take your time — when you finish reading, tick the box at the bottom to acknowledge & accept.",
        say: "Last section. Read this carefully — it's the contract between us." },
      { id: "sign", q: "Sign and confirm",
        say: "You made it. Type your name and your parent's name to sign.",
        fields: [
          { key: "studentSignature", label: "Student signature — type your full name", type: "text", required: true,
            placeholder: "Your full name" },
          { key: "parentSignature", label: "Parent signature — type their full name", type: "text", required: true,
            placeholder: "Parent's full name" },
          { key: "sigDate", label: "Date", type: "date", required: true },
          { key: "comments", label: "Any concerns or comments? (write NA if none)", type: "textarea",
            required: true, placeholder: "Write NA if you have nothing to add" },
        ] },
    ],
  },
];

/* The TUAP Services Agreement — shown in full inside the MOU screen.
   Each section becomes its own card the student scrolls through. */
export const MOU_TITLE = "Services Agreement — The University Abroad Program (TUAP)";
export const MOU_INTRO = "To enable us to better deliver our services, all parties agree to the following points:";
export const MOU_SECTIONS = [
  { h: "About Dr. Neeraj Mandhana",
    p: "Dr. Neeraj Mandhana is a reputable career & college admissions guidance professional with a practice in Mumbai, India since 2010. He specializes in mentoring teenage students towards admissions in top global colleges. Dr. Mandhana is an independent education consultant and is not affiliated with any institutions. His advice is always in the best interest of the student and the family. Dr. Mandhana is a member of the industry's top associations such as NACAC, International ACAC, HECA & IECA. Hundreds of students have benefitted from his guidance and successfully launched their careers overseas." },
  { h: "Services Included",
    p: "For high school students, Dr. Mandhana provides assistance with personal assessments, school & subject selection, college & major selection, profile development, resume making, portfolio development, application strategy, college visits, essay writing & review, financial aid application, interview prep, waitlist & final decision, transition and building a college plan. In your first session with us, a set list of meetings and dates will be allocated to you. All our work will be delivered within this framework. It will not be possible to allocate extra meetings outside of this structure." },
  { h: "Services Not Included",
    p: "Please note that we do not write any essays for you. We also do not help with visas and post-admission college paperwork. Also, while Dr. Mandhana keeps in touch with students at college & beyond, parents are expected to pay for any future follow up meetings if they need college level concerns addressed." },
  { h: "Consulting Fees Payment",
    p: "We appreciate prompt, full payment upfront before services begin. Consulting will only commence once full payment has been received. All fees are non-refundable, non-transferrable, and non-adjustable. All liability for actual payments (eg: standardized test prep, test registration, visa, camp costs, etc) is to be borne by the family." },
  { h: "Nature of Service",
    p: "As a part of our service every student is provided with an online portal. All their information and plans are regularly updated here. Parents and students are expected to view this regularly. An individual schedule will be laid out for you from the point of starting till you leave for college. While most of our work is conducted in a 1-to-1 manner, we frequently conduct workshops & residential summer camps for the benefit of our students. You also have full access to our services via email, Zoom, WhatsApp & phone call." },
  { h: "Yearly Travel",
    p: "Dr. Mandhana attends several leading conferences every year, and visits leading college campuses around the world in order to keep up to date with the latest trends in international education. He will be in limited contact during such periods. He typically travels during May, July and November." },
  { h: "Office Timings",
    p: "Monday to Friday — 10 AM to 8 PM. Request you to drop an email to us if you need any urgent help outside of this time." },
  { h: "Office Holidays",
    p: "Our office will be shut for a certain period every year for Ganpati, Diwali, Holi & Christmas. We will not be available for any meetings during this period and will only be available in a limited manner via phone, email." },
  { h: "Student & Parent Responsibilities",
    p: "Parents & Students are expected to arrive in time for the pre-allocated appointments (confirmed via email). Please do not expect the meeting to extend beyond the allocated time if you are late for a meeting. We expect clear and consistent communication from your side, all questions/concerns must be communicated via email or during an appointment, and not orally or via texts. Students are expected to follow the advice provided to the best of their ability and update Dr. Mandhana on all progress. Merely attending meetings will never get you to the college of your dreams. The student is responsible for scoring the grades that Dr. Mandhana has indicated across Grade 9-12 & in all standardized tests. Bear in mind that college admissions are almost always conditional and students must score their best right up till the end of Grade 12." },
  { h: "College Essays & College Application",
    p: "After having removed any private, school & contact information, Dr. Mandhana reserves the right to share your college application, profile & essays with his future students & wider counseling community as case study. You will benefit from access to similar past case studies, and are expected to be a part of this tradition. If you have any opposition to this, please issue a written statement to Dr. Mandhana in the first meeting with him." },
  { h: "Meeting Cancellation & Rescheduling",
    p: "If you are unable to come to a meeting, please let us know well in advance. In case of cancellations, please note that it will take us at least 2 weeks to reschedule your cancelled meeting." },
  { h: "Intellectual Property",
    p: "Over the years Neeraj Mandhana has developed various tools, college reviews, information databases, technology solutions, and other knowledge-oriented services that will be shared with you as a part of his consulting. All such intellectual property belongs to Neeraj Mandhana. You are expected to NOT share this with any third party, and delete it upon completion of our engagement." },
  { h: "Independent Consultant, NOT an Agent",
    p: "Since day one, Dr. Mandhana has been an independent education consultant. This means that he does not have any commission-bearing relationships with any college, University, or third-party vendors in the world. Your consulting fees paid to him are his primary source of income. This ethical model ensures that he remains on your side and can guide you in a way that is beneficial to you." },
  { h: "Force Majeure",
    p: "In no event shall Dr. Mandhana be responsible or liable for any failure or delay in the performance of his obligations hereunder arising out of or caused by, directly or indirectly, forces beyond his control, including, without limitation, strikes, work stoppages, accidents, acts of war or terrorism, civil or military disturbances, nuclear or natural catastrophes or acts of God, and interruptions, loss or malfunctions of utilities, communications or computer (software and hardware) services; it being understood that Dr. Mandhana shall use reasonable efforts which are consistent with accepted practices in the education sector to resume performance as soon as practicable under the circumstances." },
];

/* ── Screen visibility, completeness, progress ─────────────────────────────── */

// Flat list of every screen currently visible, with its chapter index.
export function visibleScreens(values) {
  const out = [];
  ONB_CHAPTERS.forEach((c, ci) => {
    c.screens.forEach(s => {
      if (!s.showIf || s.showIf(values)) out.push({ chapterIndex: ci, chapter: c, screen: s });
    });
  });
  return out;
}

// Fields on a screen that are currently visible given the answers so far.
export function screenFields(screen, values) {
  if (screen.kind) return [];
  return (screen.fields || []).filter(f => !f.showIf || f.showIf(values));
}

// Is a screen's required input satisfied?
export function screenComplete(screen, values) {
  if (screen.kind === "interests") {
    return INTEREST_SUBJECTS.every(s => values.interests && values.interests[s]);
  }
  if (screen.kind === "documents") {
    const docs = values.docs || {};
    const deferred = values.docsDeferred || {};
    return visibleDocuments(values).filter(d => d.required)
      .every(d => !!docs[d.key] || deferred[d.key] === true);
  }
  if (screen.kind === "community") {
    return COMMUNITY_ISSUES.every(s => values.community && values.community[s]);
  }
  if (screen.kind === "mou") {
    return values.acceptMou === "Yes";
  }
  return screenFields(screen, values)
    .filter(f => f.required)
    .every(f => {
      const v = values[f.key];
      return v !== undefined && v !== null && String(v).trim() !== "";
    });
}

// 0–100 progress across all currently-visible screens.
export function onbProgress(values) {
  const screens = visibleScreens(values);
  if (!screens.length) return 0;
  const done = screens.filter(x => screenComplete(x.screen, values)).length;
  return Math.round((done / screens.length) * 100);
}

/* ── Live Status helpers — does each person on the student record appear in
   the WhatsApp group? Ravisha's bot writes `groupMembers` (array of JIDs)
   onto the student doc whenever someone joins or leaves. ── */
function digitsOnly(p) { return (p || "").toString().replace(/\D/g, ""); }

// Match by digit-suffix so "917..." vs "+91 7..." both work.
export function isInGroup(student, phone) {
  const digits = digitsOnly(phone);
  if (!digits) return null;        // no phone to check
  // Unknown until the bot has actually reported the membership. Without this
  // an empty/absent groupMembers reads as "nobody joined", which is a lie the
  // panel then shows in red for every student.
  if (!Array.isArray(student.groupMembers)) return null;
  const members = student.groupMembers.map(String);
  return members.some(m => m.replace(/\D/g, "").endsWith(digits))
    ? true : false;
}

// The three roles whose join status we care about — { label, phone }.
export function groupRoster(student) {
  const a = student.onbAnswers || {};
  return [
    { key: "student", label: "Student",
      phone: a.studentPhone || student.studentPhone || "" },
    { key: "mother", label: "Mother",
      phone: a.motherPhone || "" },
    { key: "father", label: "Father",
      phone: a.fatherPhone || "" },
  ];
}

// One-shot Live Status summary used by the dashboard pills.
export function liveStatus(student) {
  const a = student.onbAnswers || {};
  const onbPct = student.onbComplete ? 100 : onbProgress(a);
  // The group EXISTS as soon as the bot has an id for it. The invite link is
  // written later (and can fail on its own), so keying "group created" off the
  // link alone reported "No group" for students whose group was live.
  const groupCreated = !!(student.whatsappGroupId || student.whatsappLink);
  const linkReady = !!student.whatsappLink;
  const membersKnown = Array.isArray(student.groupMembers);
  const roster = groupRoster(student);
  const joined = roster.map(r => ({ ...r, in: isInGroup(student, r.phone) }));
  const joinedCount = joined.filter(r => r.in === true).length;
  const expected = joined.filter(r => r.phone).length;
  return {
    onbPct,
    onbComplete: !!student.onbComplete,
    groupCreated,
    linkReady,
    membersKnown,
    botLeft: !!student.botLeft,
    welcomeSentAt: student.welcomeMessageSentAt || null,
    pictureSetAt: student.groupPictureSetAt || null,
    profileMeetingAt: student.profileMeetingDoneAt || null,
    joined,
    joinedCount,
    expected,
  };
}

// Per-chapter completion summary for the resume screen.
// Returns one row per chapter with `done`, `total`, `complete`, `started`.
export function chapterStatuses(values) {
  return ONB_CHAPTERS.map(c => {
    const screens = c.screens.filter(s => !s.showIf || s.showIf(values));
    const done = screens.filter(s => screenComplete(s, values)).length;
    return {
      id: c.id,
      title: c.title,
      total: screens.length,
      done,
      complete: screens.length > 0 && done === screens.length,
      started: done > 0,
    };
  });
}

/* ── Student lookup — answers admin questions from the onboarding responses,
   keyword-based (no AI). Returns matching { key, label, value } rows. ── */
const ASK_STOP = new Set(["the", "a", "an", "is", "are", "of", "do", "did", "does",
  "what", "whats", "which", "who", "tell", "me", "give", "his", "her", "their",
  "for", "and", "to", "in", "on", "about", "please", "student", "they", "was", "has",
  "how", "many", "much", "well", "you", "your", "it", "its", "have", "any", "there",
  "will", "can", "be", "am", "this", "that", "doing"]);
const ASK_TOPICS = [
  { t: ["father", "dad", "dads"], test: k => k.startsWith("father") },
  { t: ["mother", "mom", "mum", "moms"], test: k => k.startsWith("mother") },
  { t: ["parent", "parents", "family"],
    test: k => k.startsWith("father") || k.startsWith("mother") || k.startsWith("sibling")
      || k === "parentsMaritalStatus" || k === "livesWith" },
  { t: ["passport"], test: k => k.startsWith("passport") || k === "hasPassport" },
  { t: ["visa"], test: k => k.startsWith("visa") || k === "hasVisa" },
  { t: ["language", "languages", "speak"], test: k => k.startsWith("language") || k === "numLanguages" },
  { t: ["sibling", "siblings", "brother", "sister"],
    test: k => k.startsWith("sibling") || k === "hasSiblings" || k === "numSiblings" },
  { t: ["school", "schools", "education", "curriculum", "grade"],
    test: k => /^school\d/.test(k) || k === "currentGrade" || k === "grade12Finish"
      || k === "boarding" || k === "gapPeriod" || k === "admissionsSoftware" },
  { t: ["college", "institution", "university"], test: k => /inst/i.test(k) || k === "intendedMajor" },
  { t: ["birth", "birthday", "dob", "born", "birthdate", "age"],
    test: k => k === "dob" || k === "birthCountry" || k === "birthCity" },
  { t: ["phone", "mobile", "contact"], test: k => /phone/i.test(k) },
  { t: ["email", "emails", "mail"], test: k => /email/i.test(k) },
  { t: ["major", "course"], test: k => k === "intendedMajor" },
  { t: ["address", "home", "lives"], test: k => k === "address" || k === "livesWith" },
  { t: ["health", "medical", "allergy", "allergies", "disability"],
    test: k => k === "medical" || k === "disability" || k === "ldStatus" },
  { t: ["citizenship", "citizen", "nationality"], test: k => k === "citizenship" || k === "usYears" },
  { t: ["religion"], test: k => k === "religion" },
  { t: ["food", "diet"], test: k => k === "foodPref" },
  { t: ["name"], test: k => /name/i.test(k) },
];
// Answer college-list questions (rounds, deadlines, country, risk, portal).
function askColleges(cols, words, q) {
  const out = [];
  if (!cols.length) return out;
  const has = (...ws) => ws.some(w => words.includes(w));
  const list = arr => arr.map(c => c.college).join(", ") || "—";

  [["rea", "rEA"], ["ea", "EA"], ["rd", "RD"], ["rolling", "Rolling"], ["ed", "ED"]]
    .forEach(([w, r]) => {
      if (!words.includes(w)) return;
      const m = cols.filter(c => {
        const cr = (c.round || "").toLowerCase();
        return r === "ED" ? cr.startsWith("ed") : cr === r.toLowerCase();
      });
      if (m.length) out.push({ key: "rnd-" + r, label: `${r}-round colleges`, value: list(m) });
    });

  [["oct", "October 05"], ["october", "October 05"], ["nov", "November 10"],
   ["november", "November 10"], ["dec", "December 15"], ["december", "December 15"]]
    .forEach(([w, d]) => {
      if (!words.includes(w)) return;
      const m = cols.filter(c => c.deadline === d);
      out.push({ key: "dl-" + d, label: `Colleges due ${d}`, value: list(m) });
    });
  if (has("priority") && !out.some(r => r.key.startsWith("dl-"))) {
    out.push({ key: "dl-pri", label: "Priority deadline — October 05 colleges",
      value: list(cols.filter(c => c.deadline === "October 05")) });
  }

  [["usa", "USA"], ["us", "USA"], ["uk", "UK"], ["canada", "Canada"], ["europe", "Europe"],
   ["india", "India"], ["singapore", "Singapore"]].forEach(([w, cc]) => {
    if (!words.includes(w)) return;
    const m = cols.filter(c => (c.country || "") === cc);
    if (m.length) out.push({ key: "ctr-" + cc, label: `Colleges in ${cc}`, value: list(m) });
  });
  if (/countr/.test(q) && !out.length) {
    out.push({ key: "countries", label: "Countries the student is applying to",
      value: [...new Set(cols.map(c => c.country).filter(Boolean))].join(", ") || "—" });
  }

  [["low", "Low Chance"], ["reach", "Low Chance"], ["maybe", "Maybe"],
   ["should", "Should Get In"], ["safe", "Should Get In"]].forEach(([w, r]) => {
    if (!words.includes(w)) return;
    const m = cols.filter(c => c.risk === r);
    if (m.length) out.push({ key: "rsk-" + r, label: `${r} colleges`, value: list(m) });
  });

  [["commonapp", "CommonApp"], ["coalition", "Coalition"], ["ucas", "UCAS"],
   ["applyuc", "ApplyUC"], ["ouac", "OUAC"]].forEach(([w, p]) => {
    if (!words.includes(w)) return;
    const m = cols.filter(c => c.portal === p);
    if (m.length) out.push({ key: "prt-" + p, label: `${p} colleges`, value: list(m) });
  });

  if (!out.length && has("college", "colleges", "applying", "apply", "university",
    "universities", "shortlist")) {
    out.push({ key: "all-colleges", label: `College list (${cols.length})`,
      value: cols.map(c => c.college + (c.round ? ` [${c.round}]` : "")).join(", ") });
  }
  return out;
}

// Answer questions about activities, academic and credentials.
function askExtras(student, words) {
  const out = [];
  const has = (...ws) => ws.some(w => words.includes(w));

  const acts = student.activities || [];
  if (has("activity", "activities", "extracurricular", "extracurriculars", "ecs", "honors")) {
    out.push({ key: "acts-n", label: "Number of activities", value: String(acts.length) });
    if (acts.length) {
      out.push({ key: "acts-list", label: "Activities", value: acts.map((r, i) =>
        `${i + 1}. ${[r.type, r.position, r.organization].filter(Boolean).join(" — ") || "(untitled)"}`)
        .join("   ") });
    }
  }

  const ac = student.academic || {};
  if (has("major")) {
    if (ac.majorActual) out.push({ key: "maj-a", label: "Projected major", value: ac.majorActual });
    if (ac.majorClass) out.push({ key: "maj-c", label: "Major classification", value: ac.majorClass });
  }
  if (has("curriculum", "curriculums", "board")) {
    ["9", "10", "11", "12"].forEach(g => {
      const s = (ac.school || {})[g];
      if (s && s.curriculum) out.push({ key: "cur" + g, label: `Grade ${g} curriculum`, value: s.curriculum });
    });
  }
  if (has("subject", "subjects")) {
    ["9", "10", "11", "12"].forEach(g => {
      const s = (ac.school || {})[g];
      if (s && s.subjects) out.push({ key: "sub" + g, label: `Grade ${g} subjects`, value: s.subjects });
    });
  }

  const creds = student.credentials || [];
  if (has("credential", "credentials", "account", "accounts", "login", "logins",
    "password", "passwords", "username")) {
    if (!creds.length) {
      out.push({ key: "cred-none", label: "Accounts", value: "None added yet." });
    } else {
      creds.forEach((c, i) => {
        const nm = c.account === "Other" ? (c.customName || "Account") : c.account;
        out.push({ key: "cred" + i, label: `${nm} login`,
          value: `${c.username || "—"}${c.password ? "   /   " + c.password : ""}` });
      });
    }
  }
  return out;
}

export function askStudent(student, query) {
  const a = student.onbAnswers || {};
  const all = [];
  ONB_CHAPTERS.forEach(c => visibleFields(c, a).forEach(f => {
    const v = a[f.key];
    if (v !== undefined && v !== null && String(v).trim() !== "")
      all.push({ key: f.key, label: f.label, value: String(v) });
  }));
  const words = String(query || "").toLowerCase().split(/[^a-z0-9]+/)
    .filter(w => w && !ASK_STOP.has(w));
  if (!words.length) return [];

  const colHits = askColleges(student.colleges || [], words, words.join(" "));
  const extraHits = askExtras(student, words);

  let hits = [];
  ASK_TOPICS.forEach(tp => {
    if (tp.t.some(w => words.includes(w))) all.forEach(f => { if (tp.test(f.key)) hits.push(f); });
  });
  if (!hits.length && !colHits.length && !extraHits.length) {
    all.forEach(f => {
      const hay = (f.label + " " + f.value).toLowerCase();
      if (words.some(w => hay.includes(w))) hits.push(f);
    });
  }
  const seen = new Set();
  const fieldHits = hits.filter(f => (seen.has(f.key) ? false : seen.add(f.key)));
  return [...colHits, ...extraHits, ...fieldHits];
}

// Every answered field of a chapter (used by the read-only portal).
export function visibleFields(chapter, values) {
  const out = [];
  chapter.screens.forEach(s => {
    if (s.kind || (s.showIf && !s.showIf(values))) return;
    (s.fields || []).forEach(f => {
      if (!f.showIf || f.showIf(values)) out.push(f);
    });
  });
  return out;
}

/* ── Firestore: TUAP students ──────────────────────────────────────────────── */

function token() {
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

// Rajib's handover: create the TUAP student record from an FM lead (or fresh).
export async function createTuapStudent(data) {
  return await addDoc(collection(db, "tuapStudents"), {
    studentName:  data.studentName  || "",
    grade:        data.grade        || "",
    studentEmail: data.studentEmail || "",
    studentPhone: data.studentPhone || "",
    parentEmail:  data.parentEmail  || "",
    parentPhone:  data.parentPhone  || "",
    school:       data.school       || "",
    fmLeadId:     data.fmLeadId     || null,
    assignedSSO:  data.assignedSSO  || "",
    recommendedBy: data.recommendedBy || "",
    nttDone:      data.nttDone !== false,
    status:       "Handover",
    accessToken:  token(),
    slug:         slugify(data.studentName) + "-" + token().slice(0, 5),
    onbComplete:  false,
    onbChapter:   0,
    onbAnswers:   {},
    profileFlag:  "Needs Work",
    editAccess:   false,
    createdAt:    serverTimestamp(),
    updatedAt:    serverTimestamp(),
  });
}

// Queue a message for the WhatsApp bot to post into a group (default NMSquad).
// The bot is connected to Rajib's WhatsApp, so it posts from his number.
export async function queueGroupMessage(text, mentions = [], groupName = ONB_GROUP) {
  await addDoc(collection(db, "whatsappQueue"), {
    type: "groupMessage", groupName, text, mentions,
    done: false, requestedAt: serverTimestamp(),
  });
}

/* ── Ravisha's bot — job queue helpers ── */
export const RAVISHA_QUEUE = "ravishaQueue";

// Generic enqueue. The bot reads done==false jobs in order.
export async function queueRavisha(job) {
  return await addDoc(collection(db, RAVISHA_QUEUE), {
    ...job, done: false, requestedAt: serverTimestamp(),
  });
}
// Trigger the welcome-on-create ceremony after Rajib's bot has built the group.
export async function queueWelcome(studentId) {
  return await queueRavisha({ type: "welcomeOnCreate", studentId });
}
// Set a group's profile picture by name or id.
export async function queueSetGroupPicture({ groupName, groupId, dataUrl }) {
  return await queueRavisha({ type: "setGroupPicture", groupName, groupId, dataUrl });
}
// One-off DM to a phone number.
export async function queueDirect({ phone, text, mediaDataUrl, mediaName }) {
  return await queueRavisha({ type: "sendIndividual", phone, text, mediaDataUrl, mediaName });
}
// Bulk DM to many phones.
export async function queueBulk({ recipients, text, mediaDataUrl, mediaName }) {
  return await queueRavisha({ type: "bulkSend", recipients, text, mediaDataUrl, mediaName });
}
// Post into a single TUAP group.
export async function queueGroup({ groupName, text, mediaDataUrl, mediaName, mentions }) {
  return await queueRavisha({ type: "sendToGroup", groupName, text, mediaDataUrl, mediaName, mentions });
}
// Bulk: post the same content into many TUAP groups.
export async function queueGroups({ groupNames, text, mediaDataUrl, mediaName }) {
  return await queueRavisha({ type: "bulkSendToGroups", groupNames, text, mediaDataUrl, mediaName });
}
// Trigger an immediate reminders sweep (otherwise runs automatically every 6h).
export async function queueRemindersRun() {
  return await queueRavisha({ type: "runReminders" });
}

// Audit log of everything sent via the Communications Hub. Used by the
// History sub-tab so Ravisha can see what went out, to whom, and when.
export async function logSend({ kind, by, recipients, text, attachments, jobId, channel }) {
  await addDoc(collection(db, "commsLog"), {
    kind, by: by || "Ravisha", recipients: recipients || [], text: text || "",
    attachments: attachments || [], jobId: jobId || null, channel: channel || "whatsapp",
    at: serverTimestamp(),
  });
}

// Profile-meeting toggle — Ravisha marks a student's initial profile meeting
// done. Marking it done also advances the student to "Active" (the final
// pipeline stage); un-marking drops them back to "Profile Setup".
export async function setProfileMeetingDone(id, done) {
  await updateDoc(doc(db, "tuapStudents", id), {
    profileMeetingDoneAt: done ? serverTimestamp() : null,
    status: done ? "Active" : "Profile Setup",
    updatedAt: serverTimestamp(),
  });
}

// One-click "Convert to TUAP" — creates the TUAP student from an FM lead and
// fires off the entire onboarding lifecycle automatically:
//   1. create the student record (status Handover)
//   2. flag the lead
//   3. post the handover message into the NM Squad group (@Ravisha)
//   4. ask Rajib's bot to create the WhatsApp group  → status Group Created
//        (the bot then auto-triggers Ravisha's welcomeOnCreate, which sets the
//         grade group picture and posts the welcome message)
//   5. email the onboarding link to the student + parents → status ONB Sent
// `opts` lets the caller switch off the auto group / email if needed.
export async function convertLeadToTuap(lead, nttDone, opts = {}) {
  const { autoGroup = true, autoEmail = true } = opts;
  const ref = await createTuapStudent({
    studentName:  lead.studentName || "",
    grade:        lead.grade       || "",
    studentEmail: lead.email       || "",
    studentPhone: lead.phone       || "",
    parentEmail:  lead.parentEmail || "",
    parentPhone:  lead.parentPhone || "",
    school:       lead.school      || "",
    fmLeadId:     lead.firebaseId  || null,
    recommendedBy: "Rajib",
  });
  await updateDoc(doc(db, "tuapStudents", ref.id), { nttTaken: !!nttDone });
  if (lead.firebaseId) {
    await updateDoc(doc(db, "leads", lead.firebaseId), {
      convertedToTuap: true, tuapId: ref.id,
    });
  }
  // Handover message to the NMSquad group, generated to match NTT status.
  const g = (lead.gender || "").toLowerCase();
  const pron = g === "male" ? "He is" : g === "female" ? "She is" : "They are";
  const msg =
    `@917021707112\nHello Ravisha,\n${lead.studentName || "This student"} from the new FM form.\n\n` +
    `${pron} our new TUAP student, ${lead.grade || "Grade not set"}.\n\n` +
    `Onboarding kicked off automatically — WhatsApp group + onboarding email are on their way.\n\n` +
    `NTT was ${nttDone ? "done" : "not done"}.`;
  try { await queueGroupMessage(msg, [RAVISHA_WA]); } catch (e) { /* non-blocking */ }

  // Re-fetch the freshly-created student so we have slug + accessToken + phones.
  const student = await getTuapStudent(ref.id);

  // 4. Auto-create the WhatsApp group (Rajib bot → triggers welcome + pic).
  if (autoGroup && student && (student.studentPhone || student.parentPhone)) {
    try { await requestTuapGroup(student); } catch (e) { /* non-blocking */ }
  }
  // 5. Auto-send the onboarding email.
  if (autoEmail && student && student.studentEmail) {
    try { await sendOnbEmail(student); } catch (e) { /* non-blocking */ }
  }
  return ref.id;
}

// Ravisha's "Send ONB": email the onboarding link to the student.
export async function sendOnbEmail(student) {
  if (!student.studentEmail) {
    return { ok: false, error: "No student email on file." };
  }
  // Onboarding moved to the new wizard (DK 19 Jul) - link there, not the old
  // fm.neerajmandhana.com student page. (The old link still redirects via App.js.)
  const link = "https://onboarding.neerajmandhana.com/?from=" +
    encodeURIComponent(student.slug || slugify(student.studentName || "student"));
  const html =
    `<div style="font-family:Arial,sans-serif;font-size:15px;color:#222;line-height:1.65">` +
    `<p>Dear Student and Parent,</p>` +
    `<p>Welcome to <b>The University Abroad Program (TUAP)</b>. We are pleased to have you ` +
    `on board and look forward to working closely together.</p>` +
    `<p>To begin, please complete your onboarding at the link below. It captures all the ` +
    `required student, parent, academic and document information.</p>` +
    `<p><a href="${link}" style="background:#FFC400;color:#1a1400;font-weight:bold;` +
    `padding:12px 22px;border-radius:9px;text-decoration:none;display:inline-block">` +
    `Start your onboarding</a></p>` +
    `<p style="font-size:13px;color:#777">Or open this link: ${link}</p>` +
    `<p>If you have any questions, please feel free to reach out.</p>` +
    `<p>Warm regards,<br/>Neeraj Mandhana</p></div>`;
  const cc = [student.parentEmail, OFFICE_CC].filter(Boolean).join(",");
  const res = await sendMail({
    to: student.studentEmail,
    subject: "Welcome to the NM Squad Family: Your Onboarding",
    html, cc, fromName: "Neeraj Mandhana",
  });
  if (res.ok) {
    await updateDoc(doc(db, "tuapStudents", student.id), {
      status: "ONB Sent", onbEmailSentAt: serverTimestamp(),
    });
  }
  return res;
}

/* ── NTT (Natural Talent Test) — online test enrollment ────────────────────────
   "Register for NTT" on a completed FM lead. We store the registration + the
   raw results directly on the LEAD doc (leads is open-rules, and a lead can take
   the NTT without ever converting to TUAP). The student gets a unique secure link
   to the standalone test engine served at /ntt/; on completion the engine writes
   nttResults / nttStatus back onto the same lead doc. The TUAP portal later reads
   these via tuapStudent.fmLeadId. */
/* The NTT is now an Aiyyo-hosted product: students log in and take the test at
   aiyyo.in/ntt (accounts/scorecards live on the aiyyo-conference Firebase project).
   "Register for NTT" creates the student's Aiyyo account from their FM-lead data
   (incl. gender, which drives scoring), writes their ntt_students record, and emails
   their username + password from nm@neerajmandhana.com. Re-send reuses the saved pw. */
const NTT_LOGIN_URL  = "https://www.aiyyo.in/ntt";
const AIYYO_API_KEY  = "AIzaSyCm36Ezr4XV6oRHrswLwmRDCrEwbagVjAA"; // aiyyo-conference public web key

export function nttTestLink() { return NTT_LOGIN_URL; }

function nttPassword() {
  const up = "ABCDEFGHJKLMNPQRSTUVWXYZ", lo = "abcdefghijkmnpqrstuvwxyz", nu = "23456789";
  const pick = s => s[Math.floor(Math.random() * s.length)];
  return pick(up)+pick(lo)+pick(lo)+pick(nu)+pick(lo)+pick(up)+pick(nu)+pick(lo);
}

export async function registerForNTT(lead) {
  const id = lead.firebaseId || lead.id;
  if (!id) return { ok: false, error: "Lead has no id." };
  const email = (lead.email || "").trim().toLowerCase();
  if (!email) return { ok: false, error: "No student email on this lead." };
  const name = (lead.studentName || "there").trim();
  const gender = (lead.gender || "").toString().toLowerCase();

  // 1) Aiyyo NTT account — create on first register, reuse on re-send.
  let password = lead.nttPassword || nttPassword();
  let uid = lead.nttUid || null, idToken = null, created = false;
  try {
    const su = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${AIYYO_API_KEY}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    });
    const sd = await su.json();
    if (su.ok && sd.idToken) { uid = sd.localId; idToken = sd.idToken; created = true; }
    else if (sd.error && /EMAIL_EXISTS/i.test(sd.error.message || "")) {
      if (lead.nttPassword) {
        const si = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${AIYYO_API_KEY}`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password: lead.nttPassword, returnSecureToken: true }),
        });
        const sid = await si.json();
        if (si.ok && sid.idToken) { uid = sid.localId; idToken = sid.idToken; password = lead.nttPassword; }
      }
      // else: account exists but we can't sign in — the email tells them to use "Forgot password".
    } else {
      return { ok: false, error: (sd.error && sd.error.message) || "Could not create the NTT account." };
    }
  } catch (e) { return { ok: false, error: "Account service unreachable: " + (e.message || e) }; }

  // 2) Write/merge the student's record on aiyyo-conference (as themselves — own doc).
  const accountReady = !!(uid && idToken);
  if (accountReady) {
    try {
      const fields = { name:{stringValue:name}, email:{stringValue:email}, gender:{stringValue:gender}, fmLeadId:{stringValue:id} };
      const mask = ["name", "email", "gender", "fmLeadId"];
      if (created) { fields.nttStatus = { stringValue: "registered" }; fields.createdAt = { timestampValue: new Date().toISOString() }; mask.push("nttStatus", "createdAt"); }
      const url = `https://firestore.googleapis.com/v1/projects/aiyyo-conference/databases/(default)/documents/ntt_students/${uid}?` +
        mask.map(m => `updateMask.fieldPaths=${m}`).join("&");
      await fetch(url, { method: "PATCH", headers: { "Authorization": "Bearer " + idToken, "Content-Type": "application/json" }, body: JSON.stringify({ fields }) });
    } catch (e) { /* non-fatal */ }
  }

  // 3) Update the lead doc (dashboard status + saved credentials for re-send).
  const status = ["in_progress", "completed"].includes(lead.nttStatus) ? lead.nttStatus : "registered";
  try {
    await updateDoc(doc(db, "leads", id), {
      nttStatus: status, nttUid: uid || null, nttPassword: password,
      nttRegisteredAt: serverTimestamp(), nttRegisteredBy: "Rajib",
    });
  } catch (e) {}

  // 4) Email student + parent (cc office) their login + credentials from nm@neerajmandhana.com.
  const credBlock = accountReady
    ? `<table style="margin:0 0 14px;border-collapse:collapse">` +
      `<tr><td style="padding:4px 16px 4px 0;color:#777">Username</td><td style="font-weight:bold">${email}</td></tr>` +
      `<tr><td style="padding:4px 16px 4px 0;color:#777">Password</td><td style="font-weight:bold;font-family:monospace">${password}</td></tr></table>`
    : `<p>An account for <b>${email}</b> already exists. On the login page, tap <b>Forgot your password?</b> to set a new one.</p>`;
  const html =
    `<div style="font-family:Arial,sans-serif;font-size:15px;color:#222;line-height:1.65">` +
    `<p>Dear ${name} and Parent,</p>` +
    `<p>You have been <b>registered for the Natural Talent Test (NTT)</b> — a short series of interactive ` +
    `worksamples that reveal how your mind naturally works and point toward the subjects and careers ` +
    `where you'll truly thrive.</p>` +
    `<p><b>Your login</b> (at <a href="${NTT_LOGIN_URL}">${NTT_LOGIN_URL}</a>):</p>` +
    credBlock +
    `<p><b>What to expect:</b></p>` +
    `<ul style="margin:0 0 14px;padding-left:20px">` +
    `<li>It takes about <b>30–40 minutes</b> — please do it in <b>one quiet sitting</b>.</li>` +
    `<li>There is <b>nothing to study</b> and <b>no way to fail</b> — just respond naturally.</li>` +
    `<li>Use a <b>laptop or desktop</b> with a keyboard.</li>` +
    `</ul>` +
    `<p><a href="${NTT_LOGIN_URL}" style="background:#FFC400;color:#1a1400;font-weight:bold;` +
    `padding:13px 26px;border-radius:9px;text-decoration:none;display:inline-block">Sign in &amp; start your test</a></p>` +
    `<p style="font-size:13px;color:#777">These details are personal to you — please don't share them.</p>` +
    `<p>Afterwards, you'll sit down with your counsellor to walk through what your natural strengths ` +
    `mean for your studies and career.</p>` +
    `<p>Warm regards,<br/>Neeraj Mandhana</p></div>`;
  const cc = [lead.parentEmail, OFFICE_CC].filter(Boolean).join(",");
  const res = await sendMail({
    to: email, subject: "You're registered for the NM Squad Natural Talent Test (NTT)",
    html, cc, fromName: "Neeraj Mandhana",
  });
  if (res.ok) { try { await updateDoc(doc(db, "leads", id), { nttEmailSentAt: serverTimestamp() }); } catch (e) {} }
  return { ...res, link: NTT_LOGIN_URL };
}

// Read just the NTT fields off a lead doc (used by the TUAP portal NTT tab,
// which links back to the originating lead via tuapStudent.fmLeadId).
export async function getLeadNtt(leadId) {
  if (!leadId) return null;
  const s = await getDoc(doc(db, "leads", leadId));
  if (!s.exists()) return null;
  const d = s.data();
  return {
    id: s.id,
    nttToken: d.nttToken || null,
    nttStatus: d.nttStatus || null,
    nttResults: d.nttResults || null,
    nttStartedAt: d.nttStartedAt || null,
    nttCompletedAt: d.nttCompletedAt || null,
  };
}

// Student asks for edit access: flag it and email Neeraj approve/reject links.
export async function requestEditAccess(student) {
  await updateDoc(doc(db, "tuapStudents", student.id), { editRequestedAt: serverTimestamp() });
  const base = window.location.origin;
  const approve = `${base}/approve?id=${student.id}&t=${student.accessToken}&do=approve`;
  const reject = `${base}/approve?id=${student.id}&t=${student.accessToken}&do=reject`;
  const btn = (href, bg, text) =>
    `<a href="${href}" style="background:${bg};color:#fff;font-weight:bold;` +
    `padding:12px 24px;border-radius:8px;text-decoration:none;display:inline-block">${text}</a>`;
  const html =
    `<div style="font-family:Arial,sans-serif;font-size:15px;color:#222;line-height:1.6">` +
    `<p><b>${student.studentName || "A student"}</b> has requested edit access to their ` +
    `TUAP student portal.</p>` +
    `<p>${btn(approve, "#16a34a", "Approve")}&nbsp;&nbsp;${btn(reject, "#dc2626", "Reject")}</p>` +
    `<p style="font-size:12px;color:#999">Approving lets the student edit their own portal. ` +
    `NM Squad TUAP.</p></div>`;
  return await sendMail({
    to: "nm@neerajmandhana.com",
    subject: `Edit access request — ${student.studentName || "TUAP student"}`,
    html, fromName: "NM Squad TUAP",
  });
}

// Approve or reject a student's edit-access request.
export async function setEditAccess(id, granted) {
  await updateDoc(doc(db, "tuapStudents", id), {
    editAccess: !!granted, editRequestedAt: null, updatedAt: serverTimestamp(),
  });
}

// Sent once the student submits onboarding — portal + WhatsApp links.
export async function sendCompletionEmail(student) {
  if (!student.studentEmail) return { ok: false, error: "No student email." };
  const portal = studentLink(student);
  const wa = student.whatsappLink;
  const html =
    `<div style="font-family:Arial,sans-serif;font-size:15px;color:#222;line-height:1.65">` +
    `<p>Dear Student and Parent,</p>` +
    `<p>Thank you for completing your TUAP onboarding. Your student portal is now ready.</p>` +
    `<p><b>Student Portal:</b> <a href="${portal}">${portal}</a><br/>` +
    `<span style="font-size:13px;color:#777">Maintained jointly by NM Squad and you, to ` +
    `track tasks, progress and timelines.</span></p>` +
    (wa ? `<p><b>WhatsApp Group:</b> <a href="${wa}">${wa}</a></p>` : "") +
    `<p>We look forward to a productive and exciting journey together.</p>` +
    `<p>Warm regards,<br/>Neeraj Mandhana</p></div>`;
  const cc = [student.parentEmail, OFFICE_CC].filter(Boolean).join(",");
  return await sendMail({
    to: student.studentEmail,
    subject: "Your TUAP onboarding is complete",
    html, cc, fromName: "Neeraj Mandhana",
  });
}

// Queue the WhatsApp bot to auto-create this student's group
// ("<Name> (NM Squad TUAP)") and write the invite link back to the student.
export async function requestTuapGroup(student) {
  await addDoc(collection(db, "whatsappQueue"), {
    type: "createTuapGroup",
    studentId: student.id,
    studentName: student.studentName || "",
    groupName: `${student.studentName || "Student"} (NM Squad TUAP)`,
    studentPhone: student.studentPhone || "",
    parentPhone: student.parentPhone || "",
    grade: student.grade || "",
    done: false,
    requestedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, "tuapStudents", student.id), {
    whatsappRequestedAt: serverTimestamp(),
  });
}

export function subscribeTuapStudents(callback, onError) {
  const q = query(collection(db, "tuapStudents"), orderBy("createdAt", "desc"));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, onError);
}

export async function getTuapStudent(id) {
  const s = await getDoc(doc(db, "tuapStudents", id));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

// Emails allowed to open a student's portal — the student, parents, anyone
// the team shared access with, and the NM Squad team.
const TEAM_EMAILS = ["nm@neerajmandhana.com", "mandhana.office@gmail.com", "dknmsquad@gmail.com"];
export function allowedEmails(student) {
  const a = student.onbAnswers || {};
  return new Set([
    student.studentEmail, student.parentEmail, a.email, a.fatherEmail, a.motherEmail,
    ...(student.access || []).map(x => x.email),
    ...TEAM_EMAILS,
  ].filter(Boolean).map(e => String(e).trim().toLowerCase()));
}

// Resolve a student from their clean URL slug (/firstname-lastname-xxxxx).
export async function getTuapStudentBySlug(slug) {
  const qs = await getDocs(query(collection(db, "tuapStudents"), where("slug", "==", slug)));
  if (qs.empty) return null;
  const d = qs.docs[0];
  return { id: d.id, ...d.data() };
}

export function subscribeTuapStudent(id, callback, onError) {
  return onSnapshot(doc(db, "tuapStudents", id), s => {
    callback(s.exists() ? { id: s.id, ...s.data() } : null);
  }, onError);
}

export async function updateTuapStudent(id, data) {
  if (!id) return;
  await updateDoc(doc(db, "tuapStudents", id), { ...data, updatedAt: serverTimestamp() });
}

// Delete a TUAP student doc. The `files` subcollection isn't cleaned up here
// (Firestore can't recursively delete from the client). For now we accept the
// stranded sub-docs — they're orphaned and cost ~nothing.
export async function deleteTuapStudent(id) {
  if (!id) return;
  const { deleteDoc } = await import("firebase/firestore");
  await deleteDoc(doc(db, "tuapStudents", id));
}

// Group a flat list of students by their `grade` field. Returns an array of
// { grade, students } in a sensible order (Grade 9 → 10 → 11 → 12 → other).
const GRADE_ORDER = ["Grade 9", "Grade 10", "Grade 11", "Grade 12", "Gap year",
  "Transfer", "MBA", "Misc."];
export function groupByGrade(students) {
  const buckets = {};
  for (const s of students) {
    const g = (s.grade || "Other").trim() || "Other";
    if (!buckets[g]) buckets[g] = [];
    buckets[g].push(s);
  }
  const keys = Object.keys(buckets);
  keys.sort((a, b) => {
    const ia = GRADE_ORDER.indexOf(a), ib = GRADE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return keys.map(g => ({ grade: g, students: buckets[g] }));
}

// Autosave the wizard: persist answers + which screen the student is on.
export async function saveOnbDraft(id, answers, screenIndex) {
  if (!id) return;
  await updateDoc(doc(db, "tuapStudents", id), {
    onbAnswers: answers,
    onbChapter: screenIndex,
    status: "ONB In Progress",
    updatedAt: serverTimestamp(),
  });
}

// Final submit: lock the answers, derive top-level fields for the portal/list.
export async function submitOnboarding(id, answers) {
  if (!id) return;
  const full = [answers.firstName, answers.middleName, answers.lastName]
    .filter(Boolean).join(" ").trim();
  await updateDoc(doc(db, "tuapStudents", id), {
    onbAnswers:    answers,
    onbComplete:   true,
    onbSubmittedAt: serverTimestamp(),
    status:        "ONB Complete",
    studentName:   full || "",
    studentEmail:  answers.email || "",
    studentPhone:  answers.studentPhone || "",
    grade:         answers.currentGrade || "",
    intendedMajor: answers.intendedMajor || "",
    updatedAt:     serverTimestamp(),
  });
  // Notify the NMSquad group and email the family that onboarding is done.
  try {
    const s = await getTuapStudent(id);
    if (s) {
      await queueGroupMessage(`${s.studentName || "A student"} has completed the TUAP onboarding form.`);
      await sendCompletionEmail(s);
    }
  } catch (e) { /* non-blocking */ }
}

// Downscale an image in the browser and return a JPEG data URL.
function compressImage(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.width, h = img.height;
      if (w > maxDim || h > maxDim) {
        const r = Math.min(maxDim / w, maxDim / h);
        w = Math.round(w * r);
        h = Math.round(h * r);
      }
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read the image.")); };
    img.src = url;
  });
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Could not read the file."));
    r.readAsDataURL(file);
  });
}

// Upload one onboarding file: compress, then store it as its own doc in the
// student's `files` subcollection. Returns lightweight metadata (the file
// data itself is NOT put into onbAnswers, which would blow the 1 MB limit).
export async function uploadOnbFile(student, key, file) {
  const isImg = /^image\//.test(file.type || "");
  let dataUrl;
  if (isImg) {
    dataUrl = await compressImage(file, key === "picture" ? 1000 : 1700, 0.72);
  } else {
    dataUrl = await fileToDataURL(file);
  }
  if (dataUrl.length > FILE_LIMIT) {
    throw new Error("File too large. Upload a clearer photo or a smaller PDF.");
  }
  await setDoc(doc(db, "tuapStudents", student.id, "files", key), {
    data: dataUrl,
    name: file.name || key,
    type: isImg ? "image/jpeg" : (file.type || "application/octet-stream"),
    key,
    updatedAt: serverTimestamp(),
  });
  // The photo also lives on the parent doc (small) for fast portal display.
  if (key === "picture") {
    const thumb = await compressImage(file, 480, 0.7);
    await updateDoc(doc(db, "tuapStudents", student.id), { photoUrl: thumb });
  }
  return { name: file.name || key, type: file.type || "", at: Date.now() };
}

// All uploaded files for a student, keyed by document key.
export async function getTuapFiles(studentId) {
  const snap = await getDocs(collection(db, "tuapStudents", studentId, "files"));
  const m = {};
  snap.forEach(d => { m[d.id] = d.data(); });
  return m;
}
